import React from "react";

/**
 * TemplateThumb — a small SVG-based mock biodata preview for gallery cards.
 * Style variants:
 *   - "classic": serif name, motif at top, single column with maroon strip
 *   - "modern":  minimal, photo top-left, subtle horizontal band
 *   - "royal":   gold band border, dual-column, decorative flourish
 */
export default function TemplateThumb({ variant }) {
  const { template, accent, tint, motif, community } = variant;
  const commonBg = "#ffffff";

  if (template === "modern") {
    return (
      <svg
        viewBox="0 0 210 297"
        xmlns="http://www.w3.org/2000/svg"
        className="iv-template-svg"
        aria-label={`${variant.title} preview`}
      >
        <rect width="210" height="297" fill={commonBg} />
        <rect x="0" y="0" width="210" height="46" fill={tint} />
        <text x="14" y="20" fontFamily="serif" fontSize="12" fill={accent} fontWeight="700">
          {motif}
        </text>
        <text x="14" y="34" fontFamily="sans-serif" fontSize="6" fill="#8b8b8b" letterSpacing="1.5">
          BIODATA · {community.toUpperCase()}
        </text>
        <rect x="14" y="52" width="42" height="52" rx="3" fill="#e5e7eb" />
        <circle cx="35" cy="72" r="10" fill="#cbd5e1" />
        <path d="M20 105 Q35 92 50 105 Z" fill="#cbd5e1" />
        <text x="66" y="72" fontFamily="serif" fontSize="12" fill="#111" fontWeight="500">
          Aarav Sharma
        </text>
        <text x="66" y="80" fontFamily="sans-serif" fontSize="5" fill="#6b7280">
          Software Engineer · Pune
        </text>
        <line x1="66" y1="86" x2="196" y2="86" stroke="#e5e7eb" />
        {rowSet(14, 118, accent)}
        {rowSet(14, 175, accent)}
        {rowSet(14, 232, accent)}
      </svg>
    );
  }

  if (template === "royal") {
    return (
      <svg
        viewBox="0 0 210 297"
        xmlns="http://www.w3.org/2000/svg"
        className="iv-template-svg"
        aria-label={`${variant.title} preview`}
      >
        <rect width="210" height="297" fill={commonBg} />
        <rect x="8" y="8" width="194" height="281" fill="none" stroke={accent} strokeWidth="2" />
        <rect x="12" y="12" width="186" height="273" fill="none" stroke={accent} strokeWidth="0.5" />
        <text x="105" y="34" textAnchor="middle" fontFamily="serif" fontSize="14" fill={accent} fontWeight="700">
          {motif}
        </text>
        <text x="105" y="52" textAnchor="middle" fontFamily="serif" fontSize="12" fill="#111" fontWeight="700">
          Marriage Biodata
        </text>
        <text x="105" y="60" textAnchor="middle" fontFamily="sans-serif" fontSize="5" fill="#8b8b8b" letterSpacing="1.5">
          {community.toUpperCase()} · TRADITION
        </text>
        <line x1="30" y1="66" x2="180" y2="66" stroke={accent} strokeWidth="0.6" />
        <rect x="82" y="72" width="46" height="56" rx="4" fill="#f3f4f6" stroke={accent} strokeWidth="0.6" />
        <circle cx="105" cy="92" r="10" fill="#e5e7eb" />
        <text x="105" y="140" textAnchor="middle" fontFamily="serif" fontSize="9" fill="#111" fontWeight="500">
          Aarav Sharma
        </text>
        {twoColRows(22, 155, accent)}
      </svg>
    );
  }

  // classic
  return (
    <svg
      viewBox="0 0 210 297"
      xmlns="http://www.w3.org/2000/svg"
      className="iv-template-svg"
      aria-label={`${variant.title} preview`}
    >
      <rect width="210" height="297" fill={commonBg} />
      <rect x="0" y="0" width="210" height="60" fill={accent} />
      <text x="105" y="24" textAnchor="middle" fontFamily="serif" fontSize="16" fill="#fff" fontWeight="700">
        {motif}
      </text>
      <text x="105" y="42" textAnchor="middle" fontFamily="serif" fontSize="10" fill="#fff" fontWeight="700" letterSpacing="1.5">
        BIODATA
      </text>
      <text x="105" y="52" textAnchor="middle" fontFamily="sans-serif" fontSize="5" fill="#ffffff99" letterSpacing="1.5">
        {community.toUpperCase()}
      </text>
      <rect x="80" y="72" width="50" height="60" rx="2" fill="#f3f4f6" stroke={accent} strokeWidth="0.8" />
      <circle cx="105" cy="94" r="10" fill="#e5e7eb" />
      <path d="M85 132 Q105 118 125 132 Z" fill="#e5e7eb" />
      <text x="105" y="146" textAnchor="middle" fontFamily="serif" fontSize="10" fill="#111" fontWeight="700">
        Aarav Sharma
      </text>
      <line x1="60" y1="152" x2="150" y2="152" stroke={accent} strokeWidth="0.4" />
      {rowSet(14, 168, accent)}
      {rowSet(14, 220, accent)}
    </svg>
  );
}

function rowSet(x, y, accent) {
  return (
    <g>
      <text x={x} y={y} fontFamily="sans-serif" fontSize="5" fill={accent} fontWeight="700" letterSpacing="1.4">
        SECTION HEADING
      </text>
      <line x1={x} y1={y + 3} x2={x + 182} y2={y + 3} stroke="#e5e7eb" />
      {[0, 1, 2, 3, 4].map((i) => (
        <g key={i}>
          <rect x={x} y={y + 8 + i * 6} width="30" height="2" rx="1" fill="#e5e7eb" />
          <rect x={x + 36} y={y + 8 + i * 6} width={70 + (i % 3) * 20} height="2" rx="1" fill="#cbd5e1" />
        </g>
      ))}
    </g>
  );
}

function twoColRows(x, y, accent) {
  return (
    <g>
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <g key={i}>
          <rect x={x + (i % 2) * 84} y={y + Math.floor(i / 2) * 10} width={24} height="2" rx="1" fill={accent} opacity="0.35" />
          <rect x={x + (i % 2) * 84 + 28} y={y + Math.floor(i / 2) * 10} width="52" height="2" rx="1" fill="#cbd5e1" />
        </g>
      ))}
    </g>
  );
}
